import {
  PlanSimulationResponse,
  UserPackagePlan,
  PlanItem
} from 'src/models/register';
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Divider,
  Box,
  styled,
  FormControl,
  Button,
  FormHelperText,
  FormLabel,
  IconButton,
  InputAdornment,
  OutlinedInput,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { themeColors } from 'src/theme/schemes/PureLightTheme';
import separateNumberFormat from 'src/utils/separateNumberFormat';
import {
  INIT_PERIOD_OPTIONS,
  IPeriodOption
} from 'src/content/settings/Billing/ChooseYourPlan';
import { Cancel } from '@mui/icons-material';
import { useLazyQuery } from '@apollo/client';
import { GET_PLAN_SIMULATION_V2 } from 'src/services/graphql/register/query';
import { PlanSimulationV2Params } from 'src/models/register';
import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'src/app/hooks';
// import {
//   getPromoCode,
//   getUpdatedPromoCodesForPeriod
//   // promoCodesByPeriod
// } from 'src/utils/promoCodeUtils';
import useBillingSystem from 'src/services/useBillingSystem';

interface IProps {
  plan: UserPackagePlan;
  promoCodes: string[];
  planSimulation: PlanSimulationResponse;
  period: IPeriodOption;
  planItems: PlanItem[];
  onPromoUpdate?: (simulation: PlanSimulationResponse) => void;
  onReferalUpdate?: (simulation: PlanSimulationResponse) => void;
  onPeriodUpdate?: (period: IPeriodOption) => void;
  referralCode?: string;
}

interface CustomBackground {
  customBackground: string;
}

const LiteTypography = styled(Typography)<CustomBackground>(
  ({ customBackground }) => `
  background: ${customBackground};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-transform: capitalize;
  `
);

const initCountryId = '9cd88e16-510e-49f0-9ce8-f4d27f0b85bb';

const PackagePlanSummary = (props: IProps) => {
  const {
    plan,
    planSimulation: initialPlanSimulation,
    period,
    promoCodes: initialPromoCodes,
    planItems,
    onPromoUpdate,
    onReferalUpdate,
    onPeriodUpdate,
    referralCode: initialReferralCode
  } = props;

  const countryId = useAppSelector(
    (state) => state.storeBilling.currentPlan.data.countryId
  );
  const projectId = useAppSelector((state) => state.users.project.id);

  const { email } = useAppSelector((state) => state.users.userProfile.data);

  const { useGetUserSubscription } = useBillingSystem();

  const { data: subscriptionData, loading: isLoading } = useGetUserSubscription(
    { variables: { projectId: projectId } }
  );

  const isFirstPayment =
    subscriptionData?.billing_getUserSubscription?.isFirstPayment;

  const [promoCodes, setPromoCodes] = useState<string[]>(initialPromoCodes);
  const [promoCodeInput, setPromoCodeInput] = useState<string>('');
  const [isPromoError, setIsPromoError] = useState<boolean>(
    !initialPlanSimulation.isPromoValid && initialPromoCodes.length > 0
      ? true
      : false
  );

  // const [isPromoSubmitted, setIsPromoSubmitted] = useState<boolean>(false);
  const [promoCodeErrorMessage, setPromoCodeErrorMessage] = useState<string>(
    !initialPlanSimulation.isPromoValid && initialPromoCodes.length > 0
      ? initialPlanSimulation.promoErrorMessage[0]
      : ''
  );
  // const [referalCode, setReferalCode] = useState<string>(initialReferralCode);
  // const [isReferalError, setIsReferalError] = useState<boolean>(
  //   !initialPlanSimulation.isReferralValid && initialReferralCode ? true : false
  // );
  // const [isReferalSubmitted, setIsReferalSubmitted] = useState<boolean>(
  //   initialReferralCode ? true : false
  // );
  // const [referalCodeErrorMessage, setReferalCodeErrorMessage] =
  //   useState<string>(
  //     !initialPlanSimulation.isReferralValid && initialReferralCode
  //       ? initialPlanSimulation.referralErrorMessages[0]
  //       : ''
  //   );
  const [planSimulation, setPlanSimulation] = useState<PlanSimulationResponse>(
    initialPlanSimulation
  );

  // const defaultPromoCodeInCurrentPromoCodes = useMemo(() => {
  //   const allDefaultPromoCode = promoCodesByPeriod
  //     .map((item) => item.promoCode)
  //     .flat()
  //     .map((value) => value.code);

  //   return allDefaultPromoCode.find((item) => promoCodes.includes(item));
  // }, [promoCodes, promoCodesByPeriod]);

  const [getPlanSimulationV2, { loading: planSimulationLoading }] =
    useLazyQuery<
      {
        billing_getPlanSimulationV2: PlanSimulationResponse;
      },
      PlanSimulationV2Params
    >(GET_PLAN_SIMULATION_V2, {
      onCompleted: (data) => {
        if (
          data.billing_getPlanSimulationV2.isPromoValid === false &&
          data.billing_getPlanSimulationV2.promoCodes.length > 0
        ) {
          setIsPromoError(true);
          setPromoCodeErrorMessage(
            data.billing_getPlanSimulationV2.promoErrorMessage[0]
          );
          const updatedPromoCodes = promoCodes.filter(
            (item) => item !== promoCodeInput.toUpperCase()
          );
          setPromoCodes(updatedPromoCodes);
        } else {
          setPromoCodeInput('');
          setIsPromoError(false);
          onPromoUpdate?.(data.billing_getPlanSimulationV2);
        }
        if (
          data.billing_getPlanSimulationV2.isReferralValid === false &&
          data.billing_getPlanSimulationV2.referralCode
        ) {
          // setIsReferalError(true);
          // setReferalCodeErrorMessage(
          //   data.billing_getPlanSimulationV2.referralErrorMessages[0]
          // );
        } else {
          // setIsReferalError(false);
          onReferalUpdate?.(data.billing_getPlanSimulationV2);
        }
        setPlanSimulation(data.billing_getPlanSimulationV2);
      },
      fetchPolicy: 'network-only'
    });

  const handleUsePromo = (code: string) => {
    const isAlreadyExist = promoCodes.includes(code.toUpperCase());
    if (!isAlreadyExist) {
      const updatedPromoCodes = [...promoCodes, code.toUpperCase()];
      setPromoCodes(updatedPromoCodes);
      const isCustomPlan = plan.id === '2bfd2a11-06b7-4814-b4a7-bcdf10f80b59';

      getPlanSimulationV2({
        variables: {
          planItems: isCustomPlan ? planItems : null,
          countryId: countryId || initCountryId,
          period: period.value,
          promoCodes: updatedPromoCodes,
          email,
          referalCode: initialReferralCode,
          packagePlanId: !isCustomPlan ? plan.id : null
        }
      });
    }
  };

  const handleResetPromoCode = () => {
    setPromoCodeInput('');
    setIsPromoError(false);
    const isCustomPlan = plan.id === '2bfd2a11-06b7-4814-b4a7-bcdf10f80b59';

    getPlanSimulationV2({
      variables: {
        planItems: isCustomPlan ? planItems : null,
        countryId: countryId || initCountryId,
        period: period.value,
        promoCodes,
        email,
        referalCode: initialReferralCode,
        packagePlanId: !isCustomPlan ? plan.id : null
      }
    });
  };

  const handleRemovePromoCode = (codeToRemove: string) => {
    const updatedPromoCodes = promoCodes.filter(
      (code) => code !== codeToRemove
    );
    setPromoCodes(updatedPromoCodes);
    const isCustomPlan = plan.id === '2bfd2a11-06b7-4814-b4a7-bcdf10f80b59';

    getPlanSimulationV2({
      variables: {
        planItems: isCustomPlan ? planItems : null,
        countryId: countryId || initCountryId,
        period: period.value,
        promoCodes: updatedPromoCodes,
        email,
        referalCode: initialReferralCode,
        packagePlanId: !isCustomPlan ? plan.id : null
      }
    });
  };

  // const getDefaultPromoCode = (
  //   planName: string,
  //   periodValue: number
  // ): string[] => {
  //   const promoCode =
  //     promoCodesByPeriod
  //       .find((plan) => plan.planName === planName)
  //       ?.promoCode.find((promo: any) => promo.periodValue === periodValue)
  //       ?.code ?? '';
  //   return promoCode ? [promoCode] : [];
  // };

  const handleChangePeriod = (e: SelectChangeEvent<number>) => {
    const selectedPeriod = INIT_PERIOD_OPTIONS.find(
      (option) => option.value === e.target.value
    );
    if (!selectedPeriod) return;

    onPeriodUpdate(selectedPeriod);

    // const [defaultPromoCodeBySelectedPeriod] = getPromoCode(
    //   plan.name,
    //   selectedPeriod.value,
    //   isFirstPayment
    // );

    // const updatedPromoCodes = getUpdatedPromoCodesForPeriod(
    //   promoCodes,
    //   defaultPromoCodeInCurrentPromoCodes,
    //   defaultPromoCodeBySelectedPeriod
    // );

    // if (isFirstPayment) setPromoCodes(updatedPromoCodes);
    // setPromoCodes(updatedPromoCodes);
    setPromoCodes(promoCodes);

    const isCustomPlan = plan.id === '2bfd2a11-06b7-4814-b4a7-bcdf10f80b59';

    getPlanSimulationV2({
      variables: {
        planItems: isCustomPlan ? planItems : null,
        countryId: countryId || initCountryId,
        period: selectedPeriod.value,
        // promoCodes: isFirstPayment ? updatedPromoCodes : promoCodes,
        // promoCodes: updatedPromoCodes,
        promoCodes: promoCodes,
        email,
        referalCode: initialReferralCode,
        packagePlanId: !isCustomPlan ? plan.id : null
      }
    });
  };

  const getPackageColor = (planName: string) => {
    switch (planName.toLowerCase()) {
      case 'starter':
        return 'linear-gradient(135deg, #FCCF31 0%, #F55555 100%)';
      case 'lite':
        return 'linear-gradient(92.9deg, #47A3E6 -4.64%, #54CEE9 101.13%)';
      case 'advanced':
        return 'linear-gradient(135deg, #43CBFF 0%, #9708CC 100%)';
      default:
        return themeColors.blue;
    }
  };

  // const getPlanSimulationVariables = (
  //     metrics: BillableMetricsWithQuota[] | null,
  //     plans: UserPackagePlan[],
  //     promoCodesList: string[],
  //     period: number
  //   ) => {
  //     const currentPlan = getCurrentPlan(metrics, plans);
  //     const isCustomPlan = currentPlan.id === '2bfd2a11-06b7-4814-b4a7-bcdf10f80b59';

  //     return {
  //       countryId: initCountryId,
  //       period,
  //       promoCodes: promoCodesList,
  //       planItems: isCustomPlan
  //         ? metrics?.map((metric) => ({
  //             billableMetricId: metric.id,
  //             quota: metric.quota
  //           })) ?? null
  //         : null,
  //       email,
  //       referalCode: referralCode,
  //       packagePlanId: !isCustomPlan ? currentPlan.id : null
  //     };
  //   };

  useEffect(() => {
    if (initialReferralCode) {
      const isCustomPlan = plan.id === '2bfd2a11-06b7-4814-b4a7-bcdf10f80b59';

      getPlanSimulationV2({
        variables: {
          planItems: isCustomPlan ? planItems : null,
          countryId: countryId || initCountryId,
          period: period.value,
          promoCodes,
          email,
          referalCode: initialReferralCode,
          packagePlanId: !isCustomPlan ? plan.id : null
        }
      });
    }
  }, [initialReferralCode]);

  return (
    <Card sx={{ borderRadius: 2, width: '100%', maxWidth: '400px' }}>
      <CardContent>
        <Stack spacing={2}>
          {/* Plan Name */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <LiteTypography
              variant="h3"
              fontWeight={700}
              gutterBottom
              customBackground={getPackageColor(plan.displayName)}
            >
              {`${plan.displayName} - ${period.label}`}
            </LiteTypography>
            {onPeriodUpdate ? (
              <FormControl>
                <Select
                  id="period-select"
                  value={period.value}
                  onChange={(e) => handleChangePeriod(e)}
                >
                  {INIT_PERIOD_OPTIONS.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      selected={option.value === period.value}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
          </Stack>

          {/* Quotas Section */}
          <Box>
            <Stack spacing={1}>
              <Typography variant="body2" component="h4">
                {`${plan.basicObjectQuota} Social Profiles`}
              </Typography>
              <Typography variant="body2" component="h4">
                {`${plan.advanceObjectQuota} Hashtag / Keywords`}
              </Typography>
              {/* <Typography variant="body2" component="h4">
                {`${plan.entityQuota} Entities`}
              </Typography> */}
              <Typography variant="body2" component="h4" mb={3.8}>
                {`${plan.onlineMediaObjectQuota} Clippings`}
              </Typography>
            </Stack>
          </Box>

          {/* Pricing Section */}
          <Box>
            <Stack spacing={1.5}>
              {/* Subtotal */}
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Subtotal</Typography>
                <Typography variant="body2">
                  {`${planSimulation.currency.toUpperCase()} ${separateNumberFormat(
                    planSimulation.subTotal
                  )}`}
                </Typography>
              </Stack>
              <FormControl
                error={isPromoError && !planSimulationLoading}
                fullWidth
                size="medium"
              >
                <FormLabel>Promo Code</FormLabel>
                <Stack direction="row" gap={0} width="100%">
                  <OutlinedInput
                    name="promoCode"
                    onChange={(e) => {
                      setPromoCodeInput(e.target.value);
                      // if (isPromoSubmitted) {
                      //   setIsPromoSubmitted(false);
                      // }
                    }}
                    disabled={planSimulationLoading}
                    value={promoCodeInput}
                    sx={{
                      flexGrow: 1,
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0
                    }}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="clear input"
                          onClick={handleResetPromoCode}
                          edge="end"
                          disabled={!promoCodeInput}
                        >
                          <Cancel fontSize="small" sx={{ color: '#D9D9D9' }} />
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  <Button
                    variant="contained"
                    disabled={planSimulationLoading || !promoCodeInput.length}
                    sx={{
                      borderRadius: 1,
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0
                    }}
                    onClick={() => handleUsePromo(promoCodeInput)}
                  >
                    {planSimulationLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      'Use'
                    )}
                  </Button>
                </Stack>
                <FormHelperText id="promo-code-error-text">
                  {isPromoError && !planSimulationLoading
                    ? promoCodeErrorMessage
                    : null}
                </FormHelperText>
                {promoCodes.length > 0 && (
                  <Stack
                    direction="row"
                    gap={1}
                    sx={{ mt: 2, flexWrap: 'wrap' }}
                  >
                    {promoCodes.map(
                      (code) => (
                        // code !== defaultPromoCodeInCurrentPromoCodes && (
                        <Chip
                          key={code}
                          label={code}
                          onDelete={
                            // code !== defaultPromoCodeInCurrentPromoCodes
                            //   ?
                            () => handleRemovePromoCode(code)
                            // : undefined
                          }
                          sx={{
                            backgroundColor: '#E7F3F7',
                            '& .MuiChip-deleteIcon': {
                              color: themeColors.gray,
                              '&:hover': {
                                opacity: 0.7
                              }
                            }
                          }}
                        />
                      )
                      // )
                    )}
                  </Stack>
                )}
              </FormControl>

              {/* Referral Code */}
              {initialReferralCode ? (
                <FormControl
                  fullWidth
                  size="medium"
                  // error={
                  //   isReferalError &&
                  //   !planSimulationLoading &&
                  //   isReferalSubmitted
                  // }
                >
                  <FormLabel>Referral Code</FormLabel>
                  <Stack direction="row" gap={0} width="100%">
                    <OutlinedInput
                      name="referralCode"
                      // onChange={(e) => {
                      //   setReferalCode(e.target.value);
                      //   if (isReferalSubmitted) {
                      //     setIsReferalSubmitted(false);
                      //   }
                      // }}
                      disabled
                      value={initialReferralCode}
                      sx={{
                        flexGrow: 1,
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0
                      }}
                    />
                  </Stack>
                  {/* <FormHelperText id="promo-code-error-text">
                    {isReferalError &&
                    !planSimulationLoading &&
                    isReferalSubmitted
                      ? referalCodeErrorMessage
                      : null}
                  </FormHelperText> */}
                </FormControl>
              ) : null}

              {/* Discount */}
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color={themeColors.gray}>
                  Discount
                </Typography>
                <Typography
                  variant="body2"
                  color={themeColors.gray}
                  fontWeight={600}
                >
                  {`${separateNumberFormat(planSimulation.discount)}`}
                </Typography>
              </Stack>

              {/* Discount Referral */}
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color={themeColors.gray}>
                  Referral Code Discount
                </Typography>
                <Typography
                  variant="body2"
                  color={themeColors.gray}
                  fontWeight={600}
                >
                  {`${separateNumberFormat(planSimulation.referralDiscount)}`}
                </Typography>
              </Stack>
              <Divider />

              {/* Total */}
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle2" fontWeight={700}>
                  Total
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  fontSize={18}
                  color={themeColors.blue}
                >
                  {`${planSimulation.currency.toUpperCase()} ${separateNumberFormat(
                    planSimulation.total
                  )}`}
                  <Typography
                    component={'span'}
                    variant="body2"
                    fontWeight={700}
                    fontSize={18}
                    color={themeColors.darkerGray}
                    sx={{
                      textTransform: 'capitalize'
                    }}
                  >{` / ${period.period.toLowerCase()}`}</Typography>
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PackagePlanSummary;
